import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BookingPrice,
  BookingRecord,
  BookingSubStatusEnum,
  TypeBaseEnum,
} from '@pulpo/pulpo-models';
import { MatDividerModule } from '@angular/material/divider';
import {
  AccommodationRecordRoomCategoryPipe,
  ShortTextPipe,
} from '../../../../pipes';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'booking-record-bill',
  templateUrl: './booking-record-bill.component.html',
  styleUrls: ['./booking-record-bill.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatDividerModule,
    AccommodationRecordRoomCategoryPipe,
    ShortTextPipe,
    MatTooltipModule,
  ],
})
export class BookingRecordBillComponent {
  @Input() bookingRecord: BookingRecord;
  @Input() producerView = false;

  BookingSubStatusEnum = BookingSubStatusEnum;
  TypeBaseEnum = TypeBaseEnum;
  parseFloat = parseFloat;
  Number = Number;

  get effectiveBp(): BookingPrice {
    const bp = this.bookingRecord.bookingPrice;
    const qp = this.bookingRecord.archivedQuoteData?.quotedPrice;
    if (!qp) return bp;
    // fallback: the archived quote is only used when bookingPrice is missing or zero
    const fb = (bpVal: number | undefined | null, qpVal: number | undefined | null) =>
      bpVal || qpVal || 0;
    const pref = (qpVal: number | undefined | null, bpVal: number | undefined | null) =>
      qpVal || bpVal || 0;
    return {
      ...bp,
      // prix bruts : on préfère toujours la valeur du devis
      referenceFlightPrice: pref(qp.referenceFlightPrice, bp.referenceFlightPrice),
      flightPrice: pref(qp.flightPrice, bp.flightPrice),
      totalFlightPrice: pref(qp.totalFlightPrice, bp.totalFlightPrice),
      untaxedFlightPackage: pref(qp.untaxedFlightPackage, bp.untaxedFlightPackage),
      taxedFlightPackage: pref(qp.taxedFlightPackage, bp.taxedFlightPackage),
      taxPrice: pref(qp.taxPrice, bp.taxPrice),
      luggagePrice: pref(qp.luggagePrice, bp.luggagePrice),
      stayPrice: pref(qp.stayPrice, bp.stayPrice),
      packagePrice: pref(qp.packagePrice, bp.packagePrice),
      packageCommission: pref(qp.packageCommission, bp.packageCommission),
      insurancePrice: pref(qp.insurancePrice, bp.insurancePrice),
      totalOptionPrice: pref(qp.totalOptionPrice, bp.totalOptionPrice),
      totalPrice: pref(qp.totalPrice, bp.totalPrice),
      totalCommission: pref(qp.totalCommission, bp.totalCommission),
      commissionPercent: pref(qp.commissionPercent, bp.commissionPercent),
      totalInsuranceCommission: pref(qp.totalInsuranceCommission, bp.totalInsuranceCommission),
      totalLuggageCommission: pref(qp.totalLuggageCommission, bp.totalLuggageCommission),
      totalVehicleCommission: pref(qp.totalVehicleCommission, bp.totalVehicleCommission),
      toBePaidPrice: pref(qp.toBePaidPrice, bp.toBePaidPrice),
      segmentCommissions: qp.segmentCommissions?.length ? qp.segmentCommissions : (bp.segmentCommissions ?? []),
      // paid prices : fallback sur qp uniquement si bp est absent/zéro
      paidFlightPrice: fb(bp.paidFlightPrice, qp.paidFlightPrice),
      paidLuggagePrice: fb(bp.paidLuggagePrice, qp.paidLuggagePrice),
      insurancePaidPrice: fb(bp.insurancePaidPrice, qp.insurancePaidPrice),
      totalOptionPaidPrice: fb(bp.totalOptionPaidPrice, qp.totalOptionPaidPrice),
      totalRoomPrice: fb(bp.totalRoomPrice, qp.totalRoomPrice),
      totalRoomPaidPrice: fb(bp.totalRoomPaidPrice, qp.totalRoomPaidPrice),
      mealPrice: fb(bp.mealPrice, qp.mealPrice),
      mealPaidPrice: fb(bp.mealPaidPrice, qp.mealPaidPrice),
      vehiclePaidPrice: fb(bp.vehiclePaidPrice, qp.vehiclePaidPrice),
      vehiclePrice: fb(bp.vehiclePrice, qp.vehiclePrice),
      vehicleSupplementPrice: fb(bp.vehicleSupplementPrice, qp.vehicleSupplementPrice),
    };
  }

  get effectiveAccommodationRecord() {
    return this.bookingRecord.accommodationRecord ?? this.bookingRecord.archivedQuoteData?.quotedAccommodation;
  }

  get effectiveFlightRecord() {
    return this.bookingRecord.flightRecord ?? this.bookingRecord.archivedQuoteData?.quotedFlight;
  }

  get effectiveInsuranceRecord() {
    return this.bookingRecord.insuranceRecord ?? this.bookingRecord.archivedQuoteData?.quotedInsurance;
  }

  get effectiveOptionRecord() {
    return this.bookingRecord.optionRecord ?? this.bookingRecord.archivedQuoteData?.quotedOptions;
  }

  get totalPriceBought() {
    if (this.effectiveBp.toProviderPackagePriceWithMeal) {
      return this.effectiveBp.toProviderPackagePriceWithMeal.toFixed(
        2
      );
    }
    let price = this.effectiveBp.toProviderPackagePrice
      ? this.effectiveBp.toProviderPackagePrice
      : 0;
    if (
      this.effectiveBp.mealPrice &&
      this.effectiveBp.mealPrice > 0
    ) {
      price += Number(this.effectiveBp.mealPrice);
    }
    return price.toFixed(2);
  }

  get contractAndMealPrice() {
    let value = 0;
    for (let room of this.effectiveAccommodationRecord?.rooms || []) {
      if (room.price) {
        value += Number(room.price);
      }
    }
    for (let meal of this.bookingRecord.mealRecords || []) {
      if (meal.price) {
        value += Number(meal.price);
      }
    }
    return value.toFixed(2);
  }

  get contractAndMealNetPrice() {
    const bp = this.effectiveBp;
    if (bp.totalRoomPaidPrice != null) {
      return ((bp.totalRoomPaidPrice ?? 0) + (bp.mealPaidPrice ?? 0)).toFixed(2);
    }
    return this.contractAndMealPrice;
  }

  get contractAndMealBrutPrice() {
    const bp = this.effectiveBp;
    return ((bp.totalRoomPrice ?? 0) + (bp.mealPrice ?? 0)).toFixed(2);
  }

  get totalNetPrice() {
    const bp = this.effectiveBp;
    if (bp.totalRoomPaidPrice != null) {
      return (
        (bp.totalRoomPaidPrice ?? 0) +
        (bp.mealPaidPrice ?? 0) +
        (bp.paidFlightPrice ?? 0) +
        (bp.paidLuggagePrice ?? 0) +
        (bp.insurancePaidPrice ?? 0) +
        (bp.vehiclePaidPrice ?? 0) +
        (bp.totalOptionPaidPrice ?? 0)
      );
    }
    return Number(this.totalPriceBought);
  }

  get totalBrutPrice() {
    return this.effectiveBp.totalPrice ?? 0;
  }

  get flightNetPrice() {
    const bp = this.effectiveBp;
    if (bp.paidFlightPrice != null) {
      return ((bp.paidFlightPrice ?? 0) + (bp.paidLuggagePrice ?? 0)).toFixed(2);
    }
    return this.getRealFlightPrice(true);
  }

  get flightBrutPrice() {
    const bp = this.effectiveBp;
    if (bp.totalFlightPrice != null) {
      return ((bp.totalFlightPrice ?? 0) + (bp.luggagePrice ?? 0)).toFixed(2);
    }
    return ((bp.referenceFlightPrice ?? 0) + (bp.flightPrice ?? 0) + (bp.luggagePrice ?? 0)).toFixed(2);
  }

  get insuranceNetPrice() {
    const bp = this.effectiveBp;
    if (bp.insurancePaidPrice != null) {
      return (bp.insurancePaidPrice).toFixed(2);
    }
    return parseFloat(this.effectiveInsuranceRecord?.insuranceConnectorPrice || '0').toFixed(2);
  }

  get insuranceBrutPrice() {
    const bp = this.effectiveBp;
    if (bp.insurancePrice != null) {
      return (bp.insurancePrice).toFixed(2);
    }
    return parseFloat(this.effectiveInsuranceRecord?.insuranceConnectorPrice || '0').toFixed(2);
  }

  get vehicleNetPrice() {
    const bp = this.effectiveBp;
    if (bp.vehiclePaidPrice != null) {
      return (bp.vehiclePaidPrice).toFixed(2);
    }
    return (this.bookingRecord.carRentalRecord?.carRentalOfferPrice ?? 0).toFixed(2);
  }

  get vehicleBrutPrice() {
    const bp = this.effectiveBp;
    if (bp.vehiclePrice != null) {
      return ((bp.vehiclePrice ?? 0) + (bp.vehicleSupplementPrice ?? 0)).toFixed(2);
    }
    return (this.bookingRecord.carRentalRecord?.carRentalTotalPrice ?? 0).toFixed(2);
  }

  get vehicleClientPrice(): string | null {
    const bp = this.effectiveBp;
    const cr = this.bookingRecord.carRentalRecord;
    if (!cr) return null;
    if (cr.carRentalMandatory) {
      const supplement = bp?.vehicleSupplementPrice ?? 0;
      return supplement > 0 ? supplement.toFixed(2) + ' €' : null;
    }
    const price = bp?.vehicleSupplementPrice ?? cr.carRentalTotalPrice;
    return (price ?? 0).toFixed(2) + ' €';
  }

  get vehicleCommission(): string {
    const bp = this.effectiveBp;
    const cr = this.bookingRecord.carRentalRecord;
    return (bp?.totalVehicleCommission ?? cr?.carRentalCommission ?? 0).toFixed(2);
  }

  get optionNetPrice() {
    const bp = this.effectiveBp;
    if (bp.totalOptionPaidPrice != null) {
      return (bp.totalOptionPaidPrice).toFixed(2);
    }
    return (this.effectiveOptionRecord?.totalPrice ?? 0).toFixed(2);
  }

  get optionBrutPrice() {
    const bp = this.effectiveBp;
    if (bp.totalOptionPrice != null) {
      return (bp.totalOptionPrice).toFixed(2);
    }
    return (this.effectiveOptionRecord?.margedTotalPrice ?? 0).toFixed(2);
  }

  /**
   * Calcule le nombre total de bagages demandés pour le vol
   * @returns Le nombre total de bagages, 0 si aucun
   */
  getTotalLuggage(): number {
    const luggage = this.effectiveFlightRecord?.requestedLuggage
      || this.effectiveFlightRecord?.bags;

    if (!luggage || luggage.length === 0) {
      return 0;
    }

    return luggage.reduce(
      (total, reqBag) => total + (reqBag.quantity ?? 0),
      0
    );
  }

  hasLuggage(): boolean {
    return this.getTotalLuggage() > 0
      || (this.effectiveBp.luggagePrice || 0) > 0;
  }

  getRealFlightPrice(withLuggage: boolean) {
    if (withLuggage) {
      return this.effectiveBp?.taxedFlightPackage?.toFixed(2);
    } else if (this.effectiveBp?.paidFlightPrice) {
      return this.effectiveBp.paidFlightPrice.toFixed(2);
    } else {
      let luggagePrice = 0;
      if (this.effectiveBp?.paidLuggagePrice) {
        luggagePrice = this.effectiveBp.paidLuggagePrice;
      }

      if (this.effectiveFlightRecord?.offer?.bagRealPrice) {
        luggagePrice = this.effectiveFlightRecord.offer.bagRealPrice;
      }

      let price = this.effectiveBp?.taxedFlightPackage || 0;
      price -= luggagePrice;
      return price.toFixed(2);
    }
  }

  getOptionCommission(id: string) {
    if (this.effectiveBp.segmentCommissions) {
      const optionSegment =
        this.effectiveBp.segmentCommissions.find(
          (sc: any) => sc.relatedId === id
        );
      if (optionSegment) {
        return optionSegment.amount?.toFixed(2);
      }
    }
    return Number(0).toFixed(2);
  }
}
